"use client"

import { useEffect, useRef, useState } from "react"

import { twMerge } from "tailwind-merge"

import { ArrowButton } from "@/components/ArrowButton"
import { MarketRailComponent } from "@/features/home/components/MarketRailComponent"
import { MarketRailComponentItem } from "@/features/home/components/MarketRailComponentItem"
import { ProductPreviewTemplateWrapper } from "@/features/product/summary/components/ProductPreviewTemplateWrapper"
import type { Result as SearchProduct } from "@/generated/api/types.gen"
import { formatPricePlain } from "@/lib/helpers/money"
import { useBatchedViewTracking } from "@/lib/hooks/use-batched-view-tracking"
import { useInView } from "@/lib/hooks/use-in-view"
import { buildRoute } from "@/lib/routes"
import type { MarketPlaceSection } from "@/lib/types/structure"
import type { StoreRegion } from "@/platform/api/store-contract"
import { getPriceAndDiscounts } from "@/platform/catalog/products.client"
import { buildGA4ItemFromSearchProduct } from "@/platform/observability/analytics/v2/mappers"
import { trackSelectItem } from "@/platform/observability/analytics/v2/search"

export interface MarketplaceProductRailParams {
  region: StoreRegion
  section: MarketPlaceSection
  products: {
    result: SearchProduct[]
  }
  hrefOverride?: string
}

export const MarketplaceProductRail = ({ section, products, hrefOverride }: MarketplaceProductRailParams) => {
  const [isLeftArrowDisabled, setIsLeftArrowDisabled] = useState<boolean>(false)
  const [isRightArrowDisabled, setIsRightArrowDisabled] = useState<boolean>(false)
  const { title, hideItemsTitle, filter } = section
  const railRef = useRef<HTMLDivElement>(null)
  const productsRef = useRef(products.result)

  const href = hrefOverride || buildRoute.search(filter || {})

  const { trackProducts } = useBatchedViewTracking(section.id, section.title?.es || "")
  const inView = useInView(railRef, "0px", 0.3, 500)

  useEffect(() => {
    productsRef.current = products.result
  }, [products.result])

  useEffect(() => {
    if (inView && products.result.length > 0) {
      trackProducts(products.result)
    }
  }, [inView, products.result, trackProducts])

  const handleProductClick = (product: SearchProduct) => {
    const productIndex = productsRef.current.findIndex((p) => p.productGroupUuid === product.productGroupUuid)
    trackSelectItem({
      item_list_id: section.id,
      item_list_name: section.title?.es || "",
      items: [
        buildGA4ItemFromSearchProduct(product, {
          index: productIndex,
          item_list_id: section.id,
          item_list_name: section.title?.es || "",
        }),
      ],
    })
  }

  function onArrowClick(direction: "left" | "right") {
    if (!railRef.current) {
      return
    }

    if (direction === "left") {
      railRef.current.scrollBy({
        left: -railRef.current.scrollWidth,
        behavior: "smooth",
      })
    } else {
      railRef.current.scrollBy({
        left: railRef.current.scrollWidth,
        behavior: "smooth",
      })
    }
  }

  useEffect(() => {
    const node = railRef.current
    function onScroll() {
      if (!node) {
        return
      }

      if (node.scrollLeft === 0) {
        setIsLeftArrowDisabled(true)
      } else {
        setIsLeftArrowDisabled(false)
      }

      if (Math.ceil(node.scrollLeft + node.clientWidth) >= node.scrollWidth) {
        setIsRightArrowDisabled(true)
      } else {
        setIsRightArrowDisabled(false)
      }
    }

    node?.addEventListener("scroll", onScroll, { passive: true })

    return () => {
      node?.removeEventListener("scroll", onScroll)
    }
  }, [])

  return (
    <MarketRailComponent hideTitle={hideItemsTitle} title={title?.es} href={href} hrefLabel="Ver Todo">
      <div ref={railRef} className="no-scrollbar flex gap-1 overflow-x-auto px-2 py-4 sm:gap-4">
        {products?.result?.map((product: SearchProduct) => {
          const { hasFreeShipping, originalPrice, calculatedPrice, percentageDiff } = getPriceAndDiscounts(product)
          const { countResponses, averageRating } = product
          const hasRating = !!(countResponses && averageRating)

          return (
            <div key={product.productGroupUuid} className="max-w-[165px] min-w-[165px] p-2 sm:max-w-[200px] sm:min-w-[200px] md:max-w-[260px] md:min-w-[260px]">
              <MarketRailComponentItem>
                <div
                  onClick={() => handleProductClick(product)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault()
                      handleProductClick(product)
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={`View ${product.name}`}
                >
                  <ProductPreviewTemplateWrapper
                    hasFreeShipping={!!hasFreeShipping}
                    title={product.name}
                    thumbnail={product.icon.imageUrl}
                    thumbnailSize="square"
                    href={buildRoute.product(product.productUrl?.split("/")?.filter(Boolean).pop())}
                    originalPrice={formatPricePlain(originalPrice)}
                    calculatedPrice={formatPricePlain(calculatedPrice)}
                    percentageDiscount={percentageDiff}
                    rating={
                      hasRating
                        ? {
                            count_responses: countResponses,
                            average_rating: averageRating,
                          }
                        : null
                    }
                  />
                </div>
              </MarketRailComponentItem>
            </div>
          )
        })}
      </div>
      <ArrowButton
        direction="left"
        className={twMerge("arrow-breakpoint absolute inset-y-1/2 z-20 -translate-y-1/2", isLeftArrowDisabled && "opacity-50")}
        onClick={() => onArrowClick("left")}
        disabled={isLeftArrowDisabled}
      />
      <ArrowButton
        direction="right"
        className={twMerge("absolute inset-y-1/2 right-0 z-20 -translate-y-1/2 2xl:-right-8", isRightArrowDisabled && "opacity-50")}
        onClick={() => onArrowClick("right")}
        disabled={isRightArrowDisabled}
      />
    </MarketRailComponent>
  )
}
